'use client';
import React from 'react';
import classNames from 'classnames';

import {
  ArticleBlock,
  ArticleData,
  authorTypeGenerator,
  calculateReadTime,
  CampaignStrategy,
  checkIfShouldLoadCalendar,
  getCanonicalUrl,
  getPrimaryTickers,
  getPrimisIdByTaxonomies,
  setDFPTargeting,
  getAuthorName,
} from '@benzinga/article-manager';
import { isSponsoredArticle } from '@benzinga/article-manager/sponsored-utils';

import { ShareButtons } from '@benzinga/ui';
import { NoFirstRender } from '@benzinga/hooks';
import { appEnvironment, appName } from '@benzinga/utils';
import { MobileShareButtons } from './MobileShareButtons';

import { ButtonVariant, ErrorBoundary } from '@benzinga/core-ui';

import { AnalyticsContext, VisiblePageTracker } from '@benzinga/analytics';

import { ArticleMainPicture } from './ArticleMainPicture';
import { NewCampaign } from './NewCampaign';
import { ArticlePopup } from './ArticlePopup';
import { ArticleIFrame } from './ArticleIFrame';
import { ArticleBlocks } from './ArticleBlocks';
import { AuthorContent } from './AuthorContent';
import { AdvertiserDisclosure } from './AdvertiserDisclosure';
import { ArticleHeadlineContent } from './ArticleHeadlineContent';
import { ArticleBodyContent } from './ArticleBody';
import { PostedIn } from './PostedIn';
import { ArticleContentWrapper } from './ArticleLayoutChrome';

import { ArticlePageProps } from '../entities';
import { WHITELISTED_AUTHORS } from '../constants/paywall';
import { IsUserPaywalledReturn } from '@benzinga/user-context';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { freestarPlacementHeightMap } from '@benzinga/ads-utils';

const BzEdgeCTA = React.lazy(() => import('@benzinga/ads/bz-edge-cta').then(module => ({ default: module.BzEdgeCTA })));
const PaywallSurface = React.lazy(() =>
  import('@benzinga/auth-ui/paywall').then(module => ({ default: module.PaywallSurface })),
);
const RegwallSurface = React.lazy(() =>
  import('@benzinga/auth-ui/regwall').then(module => ({ default: module.RegwallSurface })),
);
const Popup = React.lazy(() => import('@benzinga/money/popup').then(module => ({ default: module.Popup })));
const KeyPoints = React.lazy(() => import('./KeyPoints'));
const ArticleCalendarWidget = React.lazy(() => import('./ArticleCalendar'));
const RelatedArticles = React.lazy(() => import('./RelatedArticles'));
const FreestarAd = React.lazy(() =>
  import('@benzinga/ads').then(module => {
    return { default: module.FreestarAd };
  }),
);

export interface ArticleLayoutMainProps extends Omit<ArticlePageProps, 'metaProps' | 'nid'> {
  articleData: ArticleData;
  articleScrollViewMoreLink?: string;
  baseUrl?: string;
  disablePartnerAdOnScroll?: boolean;
  disableWNSTNWidget?: boolean;
  hasAdLight: boolean;
  hideTopPanel?: boolean;
  isAmp?: boolean;
  isEditor: boolean;
  isBenzingaContributor: boolean;
  isBot: boolean;
  generatedByAi?: boolean;
  googleNewsUrlKey: 'benzinga' | 'benzingaIndia';
  loadMoreButtonVariant?: ButtonVariant;
  isTaggedPressRelease?: boolean;
  isDraft: boolean;
  relatedArticles?: ArticleData[];
  showAdvertiserDisclosure?: boolean;
  showApiText?: boolean;
  showFontAwesomeIcons?: boolean;
  showWhatsAppIcon?: boolean;
  executeReplaceHistory?: boolean;
  executePageviewEvent?: boolean;
  showPartnerAd?: boolean;
  campaignStrategy?: CampaignStrategy;
  campaignTicker?: string;
  trackMeta?: boolean;
  trackPageView?: boolean;
  primis?: boolean;
  disableCampaign?: boolean;
  raptiveEnabled?: boolean;
  isPaywallActive?: boolean;
  paywallStyle?: IsUserPaywalledReturn['paywallStyle'];
}

const copyright = `© ${new Date().getFullYear()} Benzinga.com. Benzinga does not provide investment advice. All rights reserved.`;

const MemoCampaign = React.memo(NewCampaign);

export const ArticleLayoutMain: React.FC<ArticleLayoutMainProps> = ({
  articleData,
  campaignSettings = {},
  campaignStrategy,
  campaignTicker,
  deviceType,
  executePageviewEvent = false,
  executeReplaceHistory = false,
  generatedByAi = false,
  googleNewsUrlKey,
  hasAdLight = false,
  hideTopPanel = false,
  isAmp = false,
  isBot = true,
  isDraft = false,
  isPaywallActive,
  isTemplate = false,
  layout,
  paywallStyle = 'paywall',
  postedInVariant = 'default',
  primis = true,
  raptiveEnabled = false,
  relatedArticles,
  showAdvertiserDisclosure = false,
  showApiText = true,
  showFontAwesomeIcons = false,
  showWhatsAppIcon = false,
  trackMeta = true,
  trackPageView = true,
  wordCount,
}) => {
  const ArticleAuthSurface = paywallStyle === 'regwall' ? RegwallSurface : PaywallSurface;
  const isTagged = React.useCallback(
    tids => {
      return articleData?.tags?.some(tag => tids.includes(tag?.tid));
    },
    [articleData?.tags],
  );
  const isChanneled = React.useCallback(
    tids => {
      return articleData?.channels?.some(channel => tids.includes(channel?.tid));
    },
    [articleData?.channels],
  );

  const authorName = getAuthorName(articleData);
  const isSponsored = isSponsoredArticle(articleData);
  const isMarketMovingExclusive = isChanneled([165347]);

  const channels = articleData.channels ? articleData.channels : [];
  const createdAt = articleData.createdAt ? new Date(articleData.createdAt).toISOString() : null;
  const contentType = articleData?.type;
  const tags = articleData.tags ? articleData.tags : [];

  const keyItems = articleData.keyItems && articleData.keyItems.map(item => item.value).filter(item => item?.length);
  const title = articleData.title;
  const renderArticleImage =
    (!!articleData.primaryImage && hasAdLight) ||
    (!!articleData.primaryImage && !!showAdvertiserDisclosure) ||
    (!!articleData.primaryImage && articleData?.type === 'pr_chainwire') ||
    (!!articleData.primaryImage && appEnvironment().isApp(appName.india));

  const primisId = getPrimisIdByTaxonomies(channels, tags);

  const onCampaignsRendered = React.useCallback(() => {
    // renderTickersBoxes('#article-body', true);
  }, []);

  const generateContentWithCampaign = React.useMemo(
    () => (blocks: ArticleBlock[]) => <ArticleBlocks blocks={blocks} />,
    [],
  );

  const articleUrl = getCanonicalUrl(articleData);
  const tickers = getPrimaryTickers(articleData?.tickers);

  const onArticleVisible = articleData => {
    setDFPTargeting(articleData);
  };

  return (
    <>
      {!hideTopPanel && (
        <div className="flex flex-col w-full items-start lg:flex-row">
          <ErrorBoundary name="article-layout-main-author-content">
            <AuthorContent
              authorLink={articleData.author?.profileUrl}
              authorName={authorName}
              authorType={authorTypeGenerator(articleData.author?.byLine ?? '', showAdvertiserDisclosure, contentType)}
              authorUid={articleData.author?.uid}
              className="flex flex-grow"
              created={createdAt ?? ''}
              isDraft={isDraft}
              readTime={calculateReadTime(articleData.body)}
              secondaryByline={articleData.author?.byLineSecondary}
              twitter={articleData.author?.twitter}
            />
          </ErrorBoundary>

          <div className="flex items-center lg:ml-auto flex-wrap gap-2">
            <ShareButtons
              buttonLocation="article_page"
              className="flex"
              showFontAwesomeIcons={showFontAwesomeIcons}
              showWhatsAppIcon={showWhatsAppIcon}
              tickers={tickers}
              title={title}
              url={articleUrl}
            />
            <NoFirstRender fallback={<div className="h-[40px] w-[128px]" />}>
              <MobileShareButtons
                deviceType={deviceType}
                googleNewsUrlKey={googleNewsUrlKey}
                showAdvertiserDisclosure={showAdvertiserDisclosure}
              />
            </NoFirstRender>
          </div>
        </div>
      )}
      {!!showAdvertiserDisclosure && <AdvertiserDisclosure isAmp={isAmp} slug={layout?.disclosure_slug} />}
      {layout && layout.popup && !isPaywallActive && <ArticlePopup popup={layout.popup} />}
      {layout?.unmonetized_interstitial && !isPaywallActive && (
        <React.Suspense fallback={null}>
          <Popup interstitial={true} popup={layout.unmonetized_interstitial} />
        </React.Suspense>
      )}
      {keyItems && keyItems.length > 0 && (
        <React.Suspense fallback={<div />}>
          <div className="key-points-wrapper">
            <KeyPoints
              data={keyItems}
              isPaywalled={
                !isBot &&
                !showAdvertiserDisclosure &&
                !WHITELISTED_AUTHORS.includes(authorName) &&
                isPaywallActive &&
                !isSponsored &&
                !isMarketMovingExclusive
              }
            />
            {!isBot &&
              !showAdvertiserDisclosure &&
              !WHITELISTED_AUTHORS.includes(authorName) &&
              isPaywallActive &&
              !isSponsored &&
              !isMarketMovingExclusive && (
                <div className="relative">
                  <BzEdgeCTA type={isTagged([866728]) ? 'whisper-index' : 'stock-of-the-day'} />
                </div>
              )}
          </div>
        </React.Suspense>
      )}
      {!hideTopPanel && (
        <ErrorBoundary name="article-layout-main-picture-block">
          <React.Suspense fallback={<div />}>
            <ArticleMainPicture
              articleData={articleData}
              imageOnly={renderArticleImage}
              primisId={primisId}
              videoPlayerSettings={layout?.video_player}
            />
          </React.Suspense>
        </ErrorBoundary>
      )}
      <ArticleContentWrapper
        className={classNames('article-content-body', { 'is-headline': articleData?.isHeadline })}
        id={`node-${articleData.nodeId}`}
      >
        <AnalyticsContext.Provider value={{ label: 'Article Above Content' }}>
          {layout && Array.isArray(layout?.content_header?.blocks) && (
            <ArticleBlocks blocks={layout.content_header.blocks} />
          )}
        </AnalyticsContext.Provider>
        {trackPageView && (
          <ErrorBoundary name="article-layout-main-visible-page-tracker">
            <VisiblePageTracker
              article={articleData}
              executeImpressionEvent={false}
              executePageviewEvent={executePageviewEvent}
              executeReplaceHistory={executeReplaceHistory}
              meta={articleData.metaProps}
              onImpress={() => onArticleVisible(articleData)}
              trackMeta={trackMeta}
            >
              <div style={{ height: 2 }} />
            </VisiblePageTracker>
          </ErrorBoundary>
        )}

        {articleData?.isHeadline ? (
          <ArticleHeadlineContent />
        ) : (
          <>
            {!isBot &&
              !showAdvertiserDisclosure &&
              !WHITELISTED_AUTHORS.includes(authorName) &&
              isPaywallActive &&
              !isSponsored &&
              !isMarketMovingExclusive && (
                <ErrorBoundary fallback={<div aria-hidden className="min-h-[280px] w-full" />} name="article-paywall">
                  <React.Suspense fallback={<div aria-hidden className="min-h-[280px] w-full" />}>
                    <ArticleAuthSurface placement="article" surface={paywallStyle} />
                  </React.Suspense>
                </ErrorBoundary>
              )}
            <AnalyticsContext.Provider value={{ label: 'Article In Content' }}>
              <ArticleBodyContent
                isBot={isBot}
                isPaywalled={
                  !isBot &&
                  !showAdvertiserDisclosure &&
                  !WHITELISTED_AUTHORS.includes(authorName) &&
                  isPaywallActive &&
                  !isMarketMovingExclusive
                }
                isSponsored={showAdvertiserDisclosure}
              >
                {isTemplate ? null : (
                  <>
                    {isBot ? (
                      <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(articleData.body) }} />
                    ) : articleData.shouldDisplayedInFrame ? (
                      <React.Suspense fallback={<div />}>
                        <ErrorBoundary name="article-layout-main-article-iframe">
                          <ArticleIFrame articleBody={articleData.body} />
                        </ErrorBoundary>
                      </React.Suspense>
                    ) : (
                      <ErrorBoundary name="article-layout-main-campaign">
                        <MemoCampaign
                          articleBlocks={articleData.blocks}
                          articleBody={articleData.parsedBody}
                          channels={channels}
                          contentType={articleData.type}
                          createdDate={articleData.createdAt}
                          deviceType={deviceType}
                          hasAdLight={hasAdLight}
                          inContentBlocks={layout?.in_content?.blocks}
                          isSponsored={showAdvertiserDisclosure}
                          nodeId={articleData.nodeId}
                          onRendered={onCampaignsRendered}
                          primis={renderArticleImage ? false : primis}
                          raptiveEnabled={raptiveEnabled}
                          strategy={campaignStrategy}
                          tags={tags}
                          ticker={campaignTicker}
                          wordCount={wordCount}
                          {...campaignSettings}
                        >
                          {generateContentWithCampaign}
                        </MemoCampaign>
                      </ErrorBoundary>
                    )}
                    {generatedByAi && (
                      <p className="block core-block">
                        <em>
                          This content was partially produced with the help of AI tools and was reviewed and published
                          by Benzinga editors.
                        </em>
                      </p>
                    )}

                    {/* eslint-disable-next-line react/jsx-no-target-blank */}
                    {showApiText && (
                      <a
                        className="text-sm my-4"
                        href="https://www.benzinga.com/apis?utm_source=benzinga.com&amp;utm_campaign=article-bottom"
                        rel="noreferrer"
                        target="_blank"
                      >
                        Market News and Data brought to you by Benzinga APIs
                      </a>
                    )}
                    <p className="copyright">{copyright}</p>
                  </>
                )}
              </ArticleBodyContent>
            </AnalyticsContext.Provider>
          </>
        )}

        {/* This should be moved to be in the article for longer articles */}
        {checkIfShouldLoadCalendar(articleData) && (
          <React.Suspense fallback={<div />}>
            <ArticleCalendarWidget article={articleData} />
          </React.Suspense>
        )}

        <React.Suspense fallback={<div />}>
          <div className="mb-4">
            <ShareButtons
              buttonLocation="article_page"
              className="flex"
              showFontAwesomeIcons={showFontAwesomeIcons}
              showWhatsAppIcon={showWhatsAppIcon}
              tickers={tickers}
              title={title}
              url={articleUrl}
            />
          </div>
        </React.Suspense>

        {trackPageView && (
          <ErrorBoundary name="article-layout-main-visible-page-tracker">
            <VisiblePageTracker
              article={articleData}
              executeImpressionEvent={false}
              executePageviewEvent={false}
              meta={articleData.metaProps}
              trackMeta={trackMeta}
            >
              <PostedIn terms={articleData.terms ?? []} variant={postedInVariant} />
            </VisiblePageTracker>
          </ErrorBoundary>
        )}

        {raptiveEnabled && (
          <React.Suspense fallback={<div className={`${freestarPlacementHeightMap.belowcontent} w-full mb-4`} />}>
            <FreestarAd className="flex items-center justify-center mb-4" onlyDesktop={true} placement="belowcontent" />
          </React.Suspense>
        )}

        <AnalyticsContext.Provider value={{ label: 'Article Below Content' }}>
          {layout && Array.isArray(layout?.content_footer?.blocks) && (
            <ArticleBlocks blocks={layout.content_footer.blocks} />
          )}
        </AnalyticsContext.Provider>

        {!articleData?.isHeadline && relatedArticles && (
          <React.Suspense fallback={<div />}>
            <RelatedArticles nodes={relatedArticles} />
          </React.Suspense>
        )}

        <hr />
      </ArticleContentWrapper>
    </>
  );
};

export { ArticleContentWrapper, ArticleLayoutWrapper } from './ArticleLayoutChrome';
